import { User, Building, Link2, DollarSign, ClipboardList, CheckCircle2, AlertCircle, Send, Loader2, FileText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { derivePayment, fmtUSD } from "./quoteMapping";

const TZ_LABELS = {
  "America/New_York": "Eastern (ET)",
  "America/Chicago": "Central (CT)",
  "America/Denver": "Mountain (MT)",
  "America/Los_Angeles": "Pacific (PT)",
  "America/Phoenix": "Arizona (AZ)",
  "America/Anchorage": "Alaska (AK)",
  "Pacific/Honolulu": "Hawaii (HI)",
  "America/Toronto": "Toronto (ET)",
  "Europe/London": "London (GMT)",
};

function isValidUrl(string) {
  try { new URL(string); return true; } 
  catch (_) { return false; }
}

function parseLinks(text) {
  if (!text) return [];
  return text.split('\n').map(l => l.trim()).filter(l => l && isValidUrl(l));
}

function ClientSection({ form }) {
  const tzLabel = TZ_LABELS[form.client_timezone] || form.client_timezone;
  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <h3 className="font-semibold mb-2 flex items-center gap-2">
        <User className="w-4 h-4" /> Client
      </h3>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-muted-foreground">Name:</span> {form.client_name}</div>
        {form.client_entity && <div><span className="text-muted-foreground">Company / LLC:</span> {form.client_entity}</div>}
        <div><span className="text-muted-foreground">Email:</span> {form.client_email}</div>
        <div><span className="text-muted-foreground">Phone:</span> {form.client_phone}</div>
        <div><span className="text-muted-foreground">Timezone:</span> {tzLabel}</div>
      </div>
    </div>
  );
}

function ProjectSection({ form }) {
  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <h3 className="font-semibold mb-2 flex items-center gap-2">
        <Building className="w-4 h-4" /> Project
      </h3>
      <div className="grid grid-cols-2 gap-2 text-sm">
        <div><span className="text-muted-foreground">Name:</span> {form.project_name}</div>
        <div><span className="text-muted-foreground">Location:</span> {form.project_city}, {form.project_state}</div>
        {form.project_address_full && (
          <div className="col-span-2"><span className="text-muted-foreground">Address:</span> {form.project_address_full}</div>
        )}
        <div className="col-span-2"><span className="text-muted-foreground">Service:</span> {form.service_summary || form.service_type}</div>
        <div><span className="text-muted-foreground">Start:</span> {form.start_date}</div>
        <div>
          <span className="text-muted-foreground">Delivery:</span> {form.expected_delivery_date}
          {form.delivery_weeks && <span className="text-muted-foreground ml-1">({form.delivery_weeks} weeks)</span>}
        </div>
      </div>
    </div>
  );
}

function LinksSection({ form }) {
  const fathomCount = parseLinks(form.fathom_links_text).length;
  const fileLinks = form.project_file_links || [];
  const services = form.additional_services || [];
  
  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <h3 className="font-semibold mb-2 flex items-center gap-2">
        <Link2 className="w-4 h-4" /> Links & Scope
      </h3>
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          Agreement: {form.agreement_type === "docusign" ? (
            <span className="text-primary">Signed PDF pulled from DocuSign</span>
          ) : form.agreement_type === "upload" && form.agreement_file ? (
            <span className="text-primary">{form.agreement_file.name} (uploaded)</span>
          ) : form.signed_agreement_link ? (
            <a href={form.signed_agreement_link} target="_blank" rel="noreferrer" className="text-primary truncate max-w-xs">{form.signed_agreement_link}</a>
          ) : (
            <span className="text-muted-foreground">Not provided</span>
          )}
        </div>
        {fathomCount > 0 && (
          <div><span className="text-muted-foreground">Fathom Links:</span> {fathomCount} recording(s)</div>
        )}
        {/* Listed explicitly: these are what the PM opens on day one, and a
            missing one is invisible until they ask where the files are. */}
        <div data-testid="review-project-file-links">
          <span className="text-muted-foreground">Project File Links:</span>{" "}
          {fileLinks.length === 0 ? (
            <span className="text-orange-500">None added</span>
          ) : (
            <span>{fileLinks.length} link(s)</span>
          )}
          {fileLinks.length > 0 && (
            <ul className="mt-1 space-y-1">
              {fileLinks.map((link, idx) => (
                <li key={idx} className="min-w-0">
                  <span className="text-xs font-medium">{link.title}</span>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-xs text-primary truncate"
                  >
                    {link.url}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>
        {form.vertical_safe_zone_required && (
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-orange-500" />
            Vertical Safe Zone Required
          </div>
        )}
        {services.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {services.map(s => (
              <Badge key={s} variant="secondary" className="text-xs">{s}</Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PaymentSection({ form, quoteMode, sourceQuote }) {
  const pay = quoteMode ? derivePayment(sourceQuote) : null;
  return (
    <div className="bg-secondary/30 rounded-lg p-4">
      <h3 className="font-semibold mb-2 flex items-center gap-2">
        <DollarSign className="w-4 h-4" /> Payment
      </h3>
      <div className="text-sm mb-2"><span className="text-muted-foreground">Method:</span> {form.payment_method}</div>
      {quoteMode && pay ? (
        <div className="rounded-md border divide-y bg-card">
          {pay.milestones.map((m, i) => (
            <div key={i} className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="text-muted-foreground truncate">{m.label}</span>
              <span className="font-medium whitespace-nowrap">{fmtUSD(m.amount)}</span>
            </div>
          ))}
          {pay.monthly > 0 && pay.term > 0 && (
            <div className="flex items-center justify-between gap-3 px-3 py-2">
              <span className="text-muted-foreground">Platform Subscription ({pay.term} mo)</span>
              <span className="font-medium whitespace-nowrap">{fmtUSD(pay.monthly)}/mo</span>
            </div>
          )}
          <div className="flex items-center justify-between gap-3 px-3 py-2 bg-secondary/40">
            <span className="font-semibold">Contract Total{pay.monthly > 0 && pay.term > 0 ? " (incl. subscription)" : ""}</span>
            <span className="font-bold">{fmtUSD(pay.grandTotal)}</span>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 text-sm">
          {form.contract_value && <div><span className="text-muted-foreground">Contract Value:</span> ${parseFloat(form.contract_value).toLocaleString()}</div>}
          {form.expected_deposit && <div><span className="text-muted-foreground">Expected Deposit:</span> ${parseFloat(form.expected_deposit).toLocaleString()}</div>}
          {form.payment_terms && <div className="col-span-2"><span className="text-muted-foreground">Terms:</span> {form.payment_terms}</div>}
        </div>
      )}
    </div>
  );
}

function SourceQuoteBanner({ sourceQuote }) {
  if (!sourceQuote) return null;
  return (
    <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2.5 text-sm">
      <FileText className="w-4 h-4 text-primary flex-shrink-0" />
      <span className="text-muted-foreground">Sourced from quote:</span>
      <span className="font-medium truncate">{sourceQuote.client_name}</span>
      {sourceQuote.created_by_name && (
        <Badge variant="secondary" className="text-[10px]">{sourceQuote.created_by_name}</Badge>
      )}
    </div>
  );
}

function NextStepsSection() {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <h3 className="font-semibold mb-2 text-blue-800">What happens next?</h3>
      <ul className="text-sm text-blue-700 space-y-1">
        <li>• A new Program will be created in "Awaiting Activation" status</li>
        <li>• Admin/Ops will review and activate the program</li>
        <li>• Ops will send the client the planning call invite automatically after activation, and follow up until they book. Please don&apos;t send the client the planning call link yourself</li>
        <li>• You&apos;ll be notified when the program is activated</li>
      </ul>
    </div>
  );
}

export default function IntakeReviewStep({ form, onBack, onSubmit, submitting, quoteMode = false, sourceQuote = null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ClipboardList className="w-5 h-5" />
          Review & Submit
        </CardTitle>
        <CardDescription>Review your submission before sending to Ops</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {quoteMode && <SourceQuoteBanner sourceQuote={sourceQuote} />}
        <ClientSection form={form} />
        <ProjectSection form={form} />
        <LinksSection form={form} />
        <PaymentSection form={form} quoteMode={quoteMode} sourceQuote={sourceQuote} />
        <NextStepsSection />
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button onClick={onSubmit} disabled={submitting} className="min-w-[200px]">
          {submitting ? (
            <span className="flex items-center">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Submitting...
            </span>
          ) : (
            <span className="flex items-center">
              <Send className="w-4 h-4 mr-2" />
              Submit Intake
            </span>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}
